import React, { useEffect, useRef, useState } from 'react';
import { ANDROID_CONFIG_GROUPS, ANDROID_SETTING_IDS, ANDROID_MANUAL_CHECKS } from './androidConfigSettings';
import useAndroidConfig from './useAndroidConfig';
import WirelessDebuggingRecoveryStatus, { WirelessDebuggingRecoveryResult, useRecoveryFacts } from './WirelessDebuggingRecoveryStatus';

const SETTING_TITLES = Object.fromEntries(ANDROID_CONFIG_GROUPS.flatMap((group) => group.settings.map((setting) => [setting.id, setting.title])));
const RESULT_LABELS = { applied: 'Applied and verified', already_set: 'Already set and verified', failed: 'Failed', unverified: 'Not verified', not_attempted: 'Not attempted' };
const RESULT_REASONS = { unsupported: 'Not supported by this firmware', permission_denied: 'Android denied permission',
  readback_mismatch: 'The value could not be verified', package_missing: 'Suitestream app not installed',
  package_ambiguous: 'Choose which installed Suitestream app to configure', service_missing: 'Suitestream accessibility service not found',
  command_failed: 'Android rejected the command', connection_lost: 'Target connection was lost', deadline_expired: 'Request expired' };
const JOB_ERRORS = { android_config_unsupported: 'This firmware does not support the requested command.',
  android_config_permission_denied: 'Android denied permission for this change.', android_config_command_failed: 'Android rejected this change.',
  no_eligible_adb_relay: 'No updated Android relay was available.', adb_restart_scope_changed: 'Connection details changed before this request ran.',
  adb_target_not_authorized: 'Verify the target connection in Pairing.',
  adb_wireless_recovery_unsupported: 'Update an eligible relay’s Suitestream app to set up Wireless Debugging recovery.',
  wireless_debugging_recovery_requires_paired_tls: 'Recovery setup requires a verified paired Wireless Debugging connection.' };

export default function DeviceAndroidConfig({ device, name, siteId, target: requestedTarget, active = false, fresh = false, onDeviceRenamed, deviceActions, deviceStatus }) {
  const target = Number(device?.id) === Number(requestedTarget?.deviceId) ? requestedTarget : null;
  const recovery = useRecoveryFacts(target?.wirelessDebuggingRecovery);
  const consoleRef = useRef(null);
  const followStatus = useRef(true);
  const [selected, setSelected] = useState(() => new Set(ANDROID_SETTING_IDS));
  const [form, setForm] = useState(null);
  const [newName, setNewName] = useState('');
  const [ssid, setSsid] = useState('');
  const [security, setSecurity] = useState('wpa2');
  const [password, setPassword] = useState('');
  const [packageName, setPackageName] = useState('');
  const [savedName, setSavedName] = useState('');
  const android = useAndroidConfig({ siteId, target, active, fresh, onRenamed: (nextName) => {
    setSavedName(nextName); onDeviceRenamed?.(device.id, nextName);
  } });
  const hasDevice = Boolean(device?.id);
  const selectedName = hasDevice ? savedName || name : 'No device selected';
  const wifi = android.config?.wifi;
  const wifiName = wifi?.connected === true && wifi.ssid ? wifi.ssid
    : wifi?.connected === false ? 'Not connected to Wi-Fi' : 'Wi-Fi status unavailable';
  const busy = android.submitting || ['queued', 'running'].includes(android.job?.status);
  const requiresPassword = ['wpa2', 'wpa3'].includes(security);
  const wifiSupported = wifi?.supportedSecurity?.includes(security) === true;
  const closeForm = () => {
    setForm(null); setNewName(''); setSsid(''); setSecurity('wpa2'); setPassword('');
  };
  const submitForm = async (event, change) => {
    event.preventDefault();
    const accepted = await android.submit(change);
    setPassword('');
    if (accepted) closeForm();
  };

  // Follow new status while allowing operators to scroll back and read older lines.
  useEffect(() => {
    if (followStatus.current && consoleRef.current) consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
  }, [deviceStatus, android.error, android.job, android.loading, android.observedAt, android.pollError]);

  return <section className="device-config__android" aria-label="Android device settings">
    <div className="device-config__android-summary">
      <div className="device-config__android-identity">
        <span className="device-config__eyebrow">Selected device</span>
        <h3>{selectedName}</h3>
        {android.config?.deviceName && android.config.deviceName !== selectedName && <p>Android name: {android.config.deviceName}</p>}
        <div className="device-config__android-actions">
          <button type="button" disabled={!hasDevice} aria-expanded={form === 'rename'}
            aria-controls="android-config-rename" onClick={() => {
              closeForm(); setNewName(android.config?.deviceName || selectedName); setForm('rename');
            }}>Rename</button>
          {deviceActions}
        </div>
      </div>
      <div className="device-config__android-identity">
        <span className="device-config__eyebrow">Connected Wi-Fi</span>
        <h3>{hasDevice ? wifiName : 'Select a device first'}</h3>
        <button type="button" disabled={!hasDevice} aria-expanded={form === 'wifi'}
          aria-controls="android-config-wifi" onClick={() => { closeForm(); setForm('wifi'); }}>Connect to different Wi-Fi</button>
      </div>
    </div>

    {hasDevice && <div className="device-config__toolbar">
      <button type="button" disabled={!target?.androidConfigAvailable || busy || android.loading}
        onClick={android.refresh}>{android.loading ? 'Reading device status…' : 'Refresh status'}</button>
    </div>}
    <div className="device-config__console">
      <div className="device-config__console-heading"><span><span className="device-config__console-light" /> Device activity</span>
        <small>{hasDevice ? selectedName : 'Awaiting selection'}</small></div>
      <div className="device-config__console-output" role="log" aria-label="Selected device activity" aria-live="polite"
        tabIndex={0} ref={consoleRef} onScroll={(event) => {
          const output = event.currentTarget;
          followStatus.current = output.scrollHeight - output.scrollTop - output.clientHeight < 24;
        }}>
        {!hasDevice && <p>Select a device to see its connection and operation status.</p>}
        {deviceStatus}
        {android.loading && <p>Reading device status…</p>}
        {android.observedAt && <p>Android status checked {new Date(android.observedAt).toLocaleTimeString()}</p>}
        {hasDevice && !target && <p>Set up ADB for the selected device in Pairing to use Android Config.</p>}
        {target && !target.androidConfigAvailable && <p>Android Config needs an online relay running the updated Suitestream app and a verified target connection in Pairing.</p>}
        {android.error && <p className="device-config__check-error">{android.error}</p>}
        {android.lostRequest && <button type="button" disabled={android.submitting} onClick={android.recover}>Check saved request</button>}
        {android.job && <div>
          <h4>{android.job.kind === 'wireless_debugging_recovery' ? 'Wireless Debugging recovery result'
            : android.job.kind === 'wifi' ? 'Wi-Fi connection request' : android.job.kind === 'rename' ? 'Device rename' : 'Settings result'}</h4>
          <p>Requested {new Date(android.job.createdAtEpochMs).toLocaleString()}</p>
          {JOB_ERRORS[android.job.error] && <p>{JOB_ERRORS[android.job.error]}</p>}
          <p>{({ queued: 'Queued', running: 'Applying…', succeeded: 'Completed', failed: 'Failed' })[android.job.status] || android.job.status}</p>
          {android.job.outcome === 'not_sent' && <p>The request was not sent. Check the connection before trying again.</p>}
          {android.job.outcome === 'delivery_uncertain' && <p>Some changes may have reached the device. The request will not be sent again automatically. Refresh status and check the device before making another request.</p>}
          {android.job.kind === 'wifi' && android.job.outcome === 'request_sent' && <p>Connection requested for {android.job.ssid}. This does not verify a successful connection. Refresh status when the device is reachable.</p>}
          {android.job.kind === 'rename' && android.job.nameSaved && <p>Renamed to {android.job.name} and verified.</p>}
          {android.job.kind === 'wireless_debugging_recovery' && <WirelessDebuggingRecoveryResult result={android.job.wirelessDebuggingRecovery}
            enabled={android.job.enabled} status={android.job.status} />}
          {android.job.error === 'android_config_name_record_failed' && <p>The Android name was changed, but its name in Suitestream could not be saved.</p>}
          {android.job.blocksRestart && android.job.deadlineEpochMs > Date.now() && android.job.status === 'failed' && <p>Wait until {new Date(android.job.deadlineEpochMs).toLocaleTimeString()} before submitting another operation.</p>}
          {android.pollError && <p>{android.pollError}</p>}
        </div>}
      </div>
    </div>

    {hasDevice && target?.authMode === 'tls' && <section className="device-config__recovery" aria-label="Wireless Debugging recovery">
      <h3>Wireless Debugging recovery</h3>
      <p>Set up once through the paired connection to restore Wireless Debugging locally after restarts. A new Wi-Fi network may require approval.</p>
      <WirelessDebuggingRecoveryStatus observation={target.wirelessDebuggingRecovery} />
      <div className="device-config__toolbar">
        <button type="button" disabled={!android.canChange || target.wirelessDebuggingRecoveryAvailable !== true
          || (recovery.fresh && target.wirelessDebuggingRecovery?.support === 'unsupported')}
          onClick={() => android.submit({ kind: 'wireless_debugging_recovery', enabled: true })}>Enable recovery</button>
        <button type="button" disabled={!android.canChange || target.wirelessDebuggingRecoveryAvailable !== true}
          onClick={() => android.submit({ kind: 'wireless_debugging_recovery', enabled: false })}>Disable recovery</button>
      </div>
      {target.wirelessDebuggingRecoveryAvailable !== true && <p>{target.wirelessDebuggingRecoveryReason === 'authorization_required'
        ? 'Verify the paired Wireless Debugging connection in Pairing before setup.'
        : 'Setup needs an online relay with the updated Suitestream recovery feature and a verified paired connection.'}</p>}
      <p>Disabling recovery stops automatic restoration. ADB reachability is shown in the connection check above.</p>
    </section>}

    {form === 'rename' && <form id="android-config-rename" className="device-config__android-form"
      aria-label={`Rename ${selectedName}`} onSubmit={(event) => submitForm(event, { kind: 'rename', name: newName })}>
      <h4>Rename {selectedName}</h4>
      <label>Device name<input autoFocus type="text" value={newName} maxLength={160}
        onChange={(event) => setNewName(event.target.value)} /></label>
      <p>Updates the Android name and the device name in Suitestream after verification.</p>
      <button type="submit" disabled={!android.canChange || !newName.trim()}>Save name</button>
      <button type="button" onClick={closeForm}>Cancel</button>
    </form>}

    {form === 'wifi' && <form id="android-config-wifi" className="device-config__android-form"
      aria-label={`Connect ${selectedName} to Wi-Fi`} onSubmit={(event) => submitForm(event,
        { kind: 'wifi', ssid, security, ...(requiresPassword ? { password } : {}) })}>
      <h4>Connect {selectedName} to different Wi-Fi</h4>
      <label>Wi-Fi name (SSID)<input autoFocus type="text" value={ssid}
        onChange={(event) => setSsid(event.target.value)} autoComplete="off" /></label>
      <label>Security<select value={security} onChange={(event) => { setSecurity(event.target.value); setPassword(''); }}>
        <option value="wpa2">WPA2</option><option value="wpa3">WPA3</option>
        <option value="owe">OWE (Enhanced Open)</option><option value="open">Open</option>
      </select></label>
      {requiresPassword && <label>Wi-Fi password<input type="password" value={password}
        onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" /></label>}
      <p>Changing Wi-Fi may disconnect this device and change its IP address. Check its connection afterward; use Pairing if the saved address needs updating.</p>
      {android.config && !wifiSupported && <p>This firmware does not advertise support for connecting to {security.toUpperCase()} networks.</p>}
      <button type="submit" disabled={!android.canChange || !ssid || !wifiSupported || (requiresPassword && !password)}>Connect</button>
      <button type="button" onClick={closeForm}>Cancel</button>
    </form>}

    {!hasDevice && <p className="device-config__empty">Choose a device in the top-right selector to configure its Android settings.</p>}
    <details className="device-config__android-disclosure">
      <summary>Developer options<span className="device-config__badge">{selected.size} of {ANDROID_SETTING_IDS.length} selected</span></summary>
      <div className="device-config__section-heading device-config__android-heading">
        <div><h3>Preferred settings</h3><p>Choose the Suitestream baseline settings to apply to this device.</p></div>
      </div>
      <div className="device-config__toolbar">
        <button type="button" onClick={() => setSelected(new Set(ANDROID_SETTING_IDS))}>Select all</button>
        <button type="button" onClick={() => setSelected(new Set())}>Clear all</button>
      </div>
      {android.config?.packages?.length > 1 && <label>Suitestream app<select value={packageName || android.config.detectedPackageName || ''}
        onChange={(event) => setPackageName(event.target.value)}>
        <option value="">Choose the installed app</option>
        {android.config.packages.map((pkg) => <option key={pkg} value={pkg}>{pkg.endsWith('.dev') ? 'Suitestream Debug' : 'Suitestream Production'}</option>)}
      </select></label>}
      <div className="device-config__android-settings">
        {ANDROID_CONFIG_GROUPS.map((group) => <fieldset key={group.title}>
          <legend>{group.title}</legend>
          {group.settings.map((setting) => <label key={setting.id} className="device-config__android-setting">
            <input type="checkbox" checked={selected.has(setting.id)} onChange={(event) => {
              const checked = event.target.checked;
              setSelected((previous) => {
                const next = new Set(previous);
                if (checked) next.add(setting.id); else next.delete(setting.id);
                return next;
              });
            }} />
            <span><strong>{setting.title}</strong><span className="device-config__android-value">{setting.value}</span>
              <small>{setting.reason}</small></span>
          </label>)}
        </fieldset>)}
      </div>
      <div className="device-config__android-apply">
        <button type="button" className="device-config__button--primary" disabled={!android.canChange || !selected.size}
          onClick={() => android.submit({ kind: 'settings', settings: Array.from(selected),
            ...((packageName || android.config?.detectedPackageName) ? { packageName: packageName || android.config.detectedPackageName } : {}) })}>
          {hasDevice ? `Apply settings to ${selectedName}` : 'Apply settings to selected device'}
        </button>
        <p>Selected settings are applied to this device only. Each change is checked; open Settings result &amp; manual setup checks for details.</p>
      </div>
    </details>
    <details className="device-config__android-disclosure">
      <summary>Settings result &amp; manual setup checks<span className="device-config__badge">{android.job?.results?.length || 0} results · {ANDROID_MANUAL_CHECKS.length} checks</span></summary>
      {android.job?.results?.length ? <>
        <h4>Settings result</h4>
        <p>Requested {new Date(android.job.createdAtEpochMs).toLocaleString()}</p>
        {android.job.results?.length > 0 && <ul className="device-config__android-results">{android.job.results.map((result) =>
          <li key={result.id} data-status={result.status}><strong>{SETTING_TITLES[result.id]}</strong><span>{RESULT_LABELS[result.status]}
            {RESULT_REASONS[result.reason] ? ` · ${RESULT_REASONS[result.reason]}` : ''}</span></li>)}</ul>}
        {android.job.results?.some((result) => result.id === 'background_processes' && result.reason === 'unsupported') && <p>Set Background process limit to Standard limit in this device’s Developer options.</p>}
      </> : <p>No settings results yet. Apply the preferred settings to check this device.</p>}
      <h4>Manual setup checks</h4>
      <p>Check these in the venue or device menus; Apply settings does not change them.</p>
      <dl className="device-config__android-checks">{ANDROID_MANUAL_CHECKS.map((item) => <div key={item.title}><dt>{item.title}</dt><dd>{item.description}</dd></div>)}</dl>
    </details>
  </section>;
}
